import React, { useCallback, useEffect, useRef, useState } from 'react';
import { API_URL } from './api';
import './UberDirect.css';

const ENDPOINT = `${API_URL}/api/admin/delivery/uber-partner`;
const STATUS_LABELS = {
  not_configured: 'Connexion partenaire à préparer',
  draft: 'Informations à compléter',
  creating: 'Demande en cours',
  reconciliation_required: 'Vérification par BEQUANIM nécessaire',
  onboarding_pending: 'Invitation demandée à Uber',
  awaiting_uber_activation: 'Activation Uber à confirmer',
  ready: 'Livraisons disponibles',
  legacy: 'Connexion Uber individuelle',
  error: 'Action nécessaire',
};

export function UberDirectStatus({ status }) {
  return <span className={`uber-direct-status ${status === 'ready' ? 'is-ready' : ''}`}>
    {STATUS_LABELS[status] || 'État à vérifier'}
  </span>;
}

export async function readUberDirectResponse(response) {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error([401, 403].includes(response.status)
      ? 'Accès refusé. Cette gestion est réservée à l’administrateur autorisé.'
      : typeof payload.error === 'string' ? payload.error : payload.message || 'Impossible de traiter la demande Uber Direct.');
    error.status = response.status;
    throw error;
  }
  return payload;
}

function createDraft(account = {}) {
  return {
    contactEmail: account.contactEmail || '',
    billingName: account.billingName || '',
    billingAddress: {
      street1: account.billingAddress?.street1 || '',
      street2: account.billingAddress?.street2 || '',
      city: account.billingAddress?.city || '',
      state: account.billingAddress?.state || '',
      zipcode: account.billingAddress?.zipcode || '',
      country_iso2: account.billingAddress?.country_iso2 || 'FR',
    },
    pickupPhone: account.pickupPhone || '',
    pickupName: account.pickupName || '',
    pickupAddressLine1: account.pickupAddressLine1 || '',
    pickupAddressLine2: account.pickupAddressLine2 || '',
    pickupCity: account.pickupCity || '',
    pickupPostalCode: account.pickupPostalCode || '',
    pickupCountry: account.pickupCountry || 'FR',
  };
}

function UberDirectSettingsPanel({ request }) {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(() => createDraft());
  const [busy, setBusy] = useState('loading');
  const [dirty, setDirty] = useState(false);
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const active = useRef(false);
  const actionPending = useRef(false);
  const applyData = useCallback((payload) => {
    setData(payload);
    setDraft(createDraft(payload.account));
    setDirty(false);
    setConsent(false);
  }, []);

  useEffect(() => {
    active.current = true;
    let cancelled = false;
    request(ENDPOINT)
      .then(readUberDirectResponse)
      .then((payload) => { if (!cancelled) applyData(payload); })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setBusy(''); });
    return () => { cancelled = true; active.current = false; };
  }, [request, applyData]);

  async function runAction(action, body) {
    if (actionPending.current) return;
    actionPending.current = true;
    setBusy(action);
    setError('');
    setNotice('');
    try {
      const isSave = action === 'save';
      const isReload = action === 'reload';
      const response = await request(`${ENDPOINT}${isSave || isReload ? '' : `/${action}`}`, isReload ? undefined : {
        method: isSave ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const payload = await readUberDirectResponse(response);
      if (!active.current) return;
      applyData(payload);
      setNotice(isReload ? 'Informations rechargées depuis BECLICK.' : isSave
        ? 'Informations enregistrées. Aucune demande n’a été envoyée à Uber.'
        : action === 'onboarding'
          ? 'Invitation demandée à Uber. Consultez votre messagerie pour poursuivre auprès d’Uber.'
          : 'État vérifié auprès d’Uber. L’activation des livraisons reste soumise à la confirmation de la facturation.');
    } catch (err) {
      if (active.current) {
        setError(err.message);
        setConsent(false);
        if ([401, 403].includes(err.status)) setData(null);
      }
      // A creation may have reached Uber despite a lost response. Reload only the
      // local state so another click cannot unknowingly create a second account.
      if (action === 'onboarding' && active.current && ![401, 403].includes(err.status)) {
        try {
          const payload = await request(ENDPOINT).then(readUberDirectResponse);
          if (active.current) applyData(payload);
        } catch (_) { /* Keep the original error; never retry the creation. */ }
      }
    } finally {
      actionPending.current = false;
      if (active.current) setBusy('');
    }
  }

  function edit(field, value, address = false) {
    setDraft((current) => address
      ? { ...current, billingAddress: { ...current.billingAddress, [field]: value } }
      : { ...current, [field]: value });
    setDirty(true);
    setConsent(false);
    setNotice('');
  }

  const account = data?.account || {};
  const platform = data?.platform || {};
  const canEdit = account.canEdit === true;
  const canInvite = platform.configured && platform.enabled && account.canCreate && !dirty && consent && !busy;
  const input = (field, label, { address = false, type = 'text', required = true, ...props } = {}) => (
    <label className="uber-direct-field" key={`${address ? 'billing-' : ''}${field}`}>
      <span>{label}{!required ? ' (facultatif)' : ''}</span>
      <input type={type} required={required} value={address ? draft.billingAddress[field] : draft[field]}
        onChange={(event) => edit(field, event.target.value, address)} {...props} />
    </label>
  );

  return <section className="uber-direct-panel" aria-labelledby="uber-direct-settings-title">
    <div className="uber-direct-heading">
      <div><span className="uber-direct-eyebrow">BECLICK · Livraison</span><h2 id="uber-direct-settings-title">Connecter Uber Direct</h2></div>
      {data && <UberDirectStatus status={account.status} />}
    </div>
    <p className="uber-direct-intro">Vos clients règlent leur commande et la livraison sur BECLICK. Dans ce parcours partenaire, Uber facture les courses directement à votre établissement. BEQUANIM assure la connexion technique.</p>
    {busy === 'loading' && <p role="status">Chargement de votre connexion…</p>}
    {error && <p className="uber-direct-alert is-error" role="alert">{error}</p>}
    {notice && <p className="uber-direct-alert" role="status">{notice}</p>}
    {error && !data && <button type="button" className="uber-direct-button is-secondary" disabled={!!busy} onClick={() => runAction('reload')}>Recharger mes informations</button>}
    {data && <>
      {(!platform.configured || !platform.enabled) && <p className="uber-direct-alert">
        {platform.message || 'BEQUANIM doit d’abord configurer et activer son accès partenaire Uber Direct. Vous pouvez préparer vos informations ; aucune invitation ne sera envoyée pour le moment.'}
      </p>}
      {platform.environment === 'sandbox' && <p className="uber-direct-alert">Environnement de test : cette connexion ne permet pas de proposer des livraisons réelles.</p>}
      {account.message && <p className="uber-direct-account-message">{account.message}</p>}
      {account.status === 'legacy' ? <p>Votre connexion Uber individuelle reste gérée depuis MASTER. Contactez BEQUANIM pour préparer le passage au parcours partenaire.</p> : <>
        <ol className="uber-direct-steps">
          <li><strong>Préparez vos informations</strong><span>Adresse de facturation et point de retrait.</span></li>
          <li><strong>Finalisez auprès d’Uber</strong><span>Après l’invitation, complétez les conditions et le moyen de paiement demandés par Uber.</span></li>
          <li><strong>Activez les livraisons</strong><span>BEQUANIM confirme l’activation après vérification de votre facturation Uber.</span></li>
        </ol>
        {!canEdit && <p className="uber-direct-alert">Ces informations sont figées pendant le raccordement à Uber. Contactez BEQUANIM pour toute correction.</p>}
        <form noValidate onSubmit={(event) => { event.preventDefault(); if (canEdit && !busy) runAction('save', draft); }}>
          <fieldset className="uber-direct-fieldset" disabled={!canEdit || !!busy}>
            <legend>Votre établissement et sa facturation</legend>
            <div className="uber-direct-fields">
              {input('contactEmail', 'Adresse e-mail pour Uber', { type: 'email', autoComplete: 'email' })}
              {input('billingName', 'Raison sociale', { autoComplete: 'organization' })}
              {input('street1', 'Adresse de facturation', { address: true, autoComplete: 'billing address-line1' })}
              {input('street2', 'Complément de facturation', { address: true, required: false, autoComplete: 'billing address-line2' })}
              {input('zipcode', 'Code postal de facturation', { address: true, autoComplete: 'billing postal-code' })}
              {input('city', 'Ville de facturation', { address: true, autoComplete: 'billing address-level2' })}
              {input('state', 'Région de facturation', { address: true, required: false })}
              {input('country_iso2', 'Pays de facturation (code à 2 lettres)', { address: true, maxLength: 2, autoCapitalize: 'characters', autoComplete: 'billing country' })}
            </div>
          </fieldset>
          <fieldset className="uber-direct-fieldset" disabled={!canEdit || !!busy}>
            <legend>Point de retrait des commandes</legend>
            <div className="uber-direct-fields">
              {input('pickupName', 'Nom du restaurant')}
              {input('pickupPhone', 'Téléphone du restaurant', { type: 'tel', placeholder: '+33…', autoComplete: 'tel' })}
              {input('pickupAddressLine1', 'Adresse de retrait')}
              {input('pickupAddressLine2', 'Complément de retrait', { required: false })}
              {input('pickupPostalCode', 'Code postal de retrait')}
              {input('pickupCity', 'Ville de retrait')}
              {input('pickupCountry', 'Pays de retrait (code à 2 lettres)', { maxLength: 2, autoCapitalize: 'characters' })}
            </div>
          </fieldset>
          {canEdit && <div className="uber-direct-actions">
            <button type="submit" className="uber-direct-button is-secondary" disabled={!!busy || !dirty}>{busy === 'save' ? 'Enregistrement…' : 'Enregistrer mes informations'}</button>
            <span>Cette étape enregistre uniquement vos informations dans BECLICK.</span>
          </div>}
        </form>
        {!account.organizationId && !['creating', 'reconciliation_required'].includes(account.status) && <div className="uber-direct-onboarding">
          <label className="uber-direct-consent"><input type="checkbox" checked={consent} disabled={!!busy || !account.canCreate || dirty || !platform.configured || !platform.enabled} onChange={(event) => setConsent(event.target.checked)} />
            <span>J’autorise la transmission de ces informations à Uber pour créer mon accès Uber Direct. Je confirme que mon établissement réglera directement à Uber les frais de livraison et finalisera les conditions et le moyen de paiement auprès d’Uber.</span>
          </label>
          {dirty && <p className="uber-direct-hint">Enregistrez vos modifications avant de demander l’invitation.</p>}
          {!dirty && account.missingFields?.length > 0 && <p className="uber-direct-hint">Complétez et enregistrez les informations obligatoires pour demander votre invitation.</p>}
          <button type="button" className="uber-direct-button" disabled={!canInvite} onClick={() => runAction('onboarding', { consent: true })}>{busy === 'onboarding' ? 'Demande à Uber…' : 'Demander mon invitation Uber Direct'}</button>
        </div>}
      </>}
      {account.canRefresh && <div className="uber-direct-actions">
        <button type="button" className="uber-direct-button is-secondary" disabled={!!busy || dirty} onClick={() => runAction('refresh', {})}>{busy === 'refresh' ? 'Vérification…' : 'Vérifier mon compte auprès d’Uber'}</button>
        <span>Cette vérification ne confirme pas à elle seule l’activation de la facturation.</span>
      </div>}
    </>}
  </section>;
}

export default function UberDirectSettings({ request = fetch, scopeKey = '' }) {
  return <UberDirectSettingsPanel key={scopeKey} request={request} />;
}
